import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { RiskBadge } from "../components/RiskBadge";
import { STATUS_LABEL, computeSla } from "../lib/constants";
import { usePlantao } from "../components/PlantaoToggle";
import NextPlantoesWidget from "../components/consultant/NextPlantoesWidget";
import NextAppointmentsWidget from "../components/consultant/NextAppointmentsWidget";
import { ConsultantFinanceWidget } from "../components/consultant/ConsultantFinanceWidget";
import ConsultorAgendaWidget from "../components/consultant/ConsultorAgendaWidget";
import CentralCasesWidget from "../components/consultant/CentralCasesWidget";
import { toast } from "sonner";
import { Users, Activity, BarChart3, Download, Search, Siren, Clock, Scale, DollarSign, ShieldCheck, Ban, FileText, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell, PieChart, Pie } from "recharts";

import { useAuth } from "../context/AuthContext";
const WEEKDAYS_PT = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const MyScheduleWidget = () => {
  const { user: currentUser } = useAuth();
  const [slots, setSlots] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [swapSlot, setSwapSlot] = useState(null);
  const [swapForm, setSwapForm] = useState({ target: "", reason: "" });

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/consultant/me/schedule"),
        api.get("/consultant/list"),
      ]);
      setSlots(s.data); setColleagues(u.data);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitSwap = async (e) => {
    e.preventDefault();
    if (!swapForm.target) return toast.error("Escolha um colega.");
    try {
      await api.post(`/consultant/schedule/${swapSlot.id}/swap-request`, {
        target_consultor_id: swapForm.target, reason: swapForm.reason,
      });
      toast.success("Solicitação de troca enviada ao admin.");
      setSwapSlot(null); setSwapForm({ target: "", reason: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const confirmSlot = async (slotId) => {
    try {
      await api.post(`/consultant/schedule/${slotId}/confirm`);
      toast.success("Presença confirmada — obrigado!");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  if (slots.length === 0) return null;
  return (
    <section id="plantoes" className="bg-white border border-slate-200 rounded-xl p-5 mb-6 scroll-mt-16" data-testid="my-schedule-widget">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-semibold text-lg text-[#0B2B62]">Meus plantões da semana</h2>
        <span className="text-xs text-slate-500">{slots.length} slot{slots.length > 1 ? "s" : ""}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {slots.map((s) => {
          const confirmed = !!s.confirmed_at;
          return (
            <div key={s.id} className={`p-3 border rounded-md ${confirmed ? "border-green-300 bg-green-50" : "border-slate-200 bg-slate-50"}`} data-testid={`myslot-${s.id}`}>
              <div className="text-xs uppercase tracking-widest text-slate-500">{WEEKDAYS_PT[s.weekday]}</div>
              <div className="font-mono font-semibold text-[#0B2B62] mt-1">{String(s.start_hour).padStart(2, "0")}:00 – {String(s.end_hour).padStart(2, "0")}:00</div>
              {confirmed ? (
                <div className="mt-2 text-[10px] text-green-700 font-bold uppercase tracking-widest inline-flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.6l7.3-7.3a1 1 0 0 1 1.4 0z" clipRule="evenodd"/></svg>
                  Presença confirmada
                </div>
              ) : (
                <button onClick={() => confirmSlot(s.id)} data-testid={`confirm-slot-${s.id}`}
                  className="mt-2 text-xs text-emerald-700 font-semibold hover:underline">
                  Confirmar presença ✓
                </button>
              )}
              <button onClick={() => setSwapSlot(s)} data-testid={`swap-btn-${s.id}`} className="mt-1 block text-xs text-blue-700 hover:underline">Trocar com colega →</button>
            </div>
          );
        })}
      </div>

      {swapSlot && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSwapSlot(null)}>
          <form onSubmit={submitSwap} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 max-w-md w-full" data-testid="swap-modal">
            <h3 className="font-display font-bold text-lg text-[#0B2B62] mb-3">Solicitar troca de plantão</h3>
            <p className="text-xs text-slate-600 mb-3">{WEEKDAYS_PT[swapSlot.weekday]} · {String(swapSlot.start_hour).padStart(2, "0")}:00–{String(swapSlot.end_hour).padStart(2, "0")}:00</p>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Colega</label>
            <select value={swapForm.target} onChange={(e) => setSwapForm({ ...swapForm, target: e.target.value })} data-testid="swap-target"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm mb-3" required>
              <option value="">— selecione —</option>
              {colleagues.filter((c) => c.id && c.id !== currentUser?.id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Motivo (opcional)</label>
            <textarea rows={2} value={swapForm.reason} onChange={(e) => setSwapForm({ ...swapForm, reason: e.target.value })} data-testid="swap-reason"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm mb-4" placeholder="Ex: compromisso familiar" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSwapSlot(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
              <button type="submit" data-testid="swap-submit" className="px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold">Solicitar</button>
            </div>
            <p className="text-[10px] text-slate-400 mt-3">A troca só efetiva após aprovação do admin RAF.</p>
          </form>
        </div>
      )}
    </section>
  );
};

const ConsultantDashboard = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const plantaoOn = usePlantao();
  const [cases, setCases] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [days, setDays] = useState(null); // null=all, 7, 30, 90
  const [consultantFilter, setConsultantFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [kickHistory, setKickHistory] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [invites, setInvites] = useState([]);
  const [streak, setStreak] = useState(null);
  const prevEmergencyIds = useRef(new Set());
  const audioCtxRef = useRef(null);

  useEffect(() => {
    api.get("/consultant/me/kick-history").then((r) => setKickHistory(r.data)).catch(() => {});
    api.get("/consultant/me/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
    api.get("/consultant/on-call/invites").then((r) => setInvites(r.data)).catch(() => {});
    api.get("/consultant/me/streak").then((r) => setStreak(r.data)).catch(() => {});
  }, []);

  const acceptInvite = async (id) => {
    try {
      await api.post(`/consultant/on-call/invite/${id}/accept`);
      toast.success("Plantão aceito — Modo Plantão ativo");
      setInvites((v) => v.filter((x) => x.id !== id));
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao aceitar"); }
  };

  const dismissOnboarding = async () => {
    try { await api.post("/consultant/me/onboarding/dismiss"); setOnboarding(null); }
    catch { setOnboarding(null); }
  };

  // Beep via Web Audio quando um novo caso emergencial ativo entra na lista
  // — apenas quando o consultor está em modo Plantão.
  const beep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      // Duplo bip curto — 880Hz depois 660Hz
      [880, 660].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + i * 0.22);
        gain.gain.exponentialRampToValueAtTime(0.28, now + i * 0.22 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.22 + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.22);
        osc.stop(now + i * 0.22 + 0.2);
      });
    } catch (_) { /* som é best-effort */ }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const suffix = days ? `?days=${days}` : "";
      const [c, s, cons, cap] = await Promise.all([
        api.get("/consultant/all"),
        api.get(`/consultant/stats${suffix}`),
        api.get("/consultant/list"),
        api.get("/consultant/me/capacity").catch(() => ({ data: null })),
      ]);
      setCases(c.data);
      setStats(s.data);
      setConsultants(cons.data);
      setCapacity(cap.data);
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Polling a cada 15s + detecção de NOVO caso emergencial ativo → beep se plantão ON
  useEffect(() => {
    const id = setInterval(() => { fetchAll(); }, 15000);
    return () => clearInterval(id);
  }, [fetchAll]);

  useEffect(() => {
    const active = cases.filter((c) => c.risk_level === "emergencial" && c.status !== "concluido");
    const currentIds = new Set(active.map((c) => c.id));
    if (prevEmergencyIds.current.size > 0) {
      const novel = [...currentIds].filter((id) => !prevEmergencyIds.current.has(id));
      if (novel.length > 0 && plantaoOn) {
        beep();
        toast.error(`⚠️ ${novel.length} novo(s) caso(s) emergencial(is) — plantão`, { duration: 8000 });
      }
    }
    prevEmergencyIds.current = currentIds;
  }, [cases, plantaoOn, beep]);

  const exportCsv = () => {
    api.get(`/consultant/stats.csv${days ? `?days=${days}` : ""}`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `raf_stats${days ? "_"+days+"d" : "_all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  // Memoize heavy filter/count computations — recomputes only when cases change.
  const stats_counts = useMemo(() => {
    const active = cases.filter((c) => c.status !== "concluido").length;
    const emerg = cases.filter((c) => c.risk_level === "emergencial" && c.status !== "concluido").length;
    const closed = cases.filter((c) => c.status === "concluido").length;
    return { active, emerg, closed };
  }, [cases]);

  const filtered = useMemo(() => cases.filter((c) => {
    if (filter !== "all") {
      if (filter === "active" && c.status === "concluido") return false;
      if (filter === "closed" && c.status !== "concluido") return false;
      if (["emergencial","alta","moderada","programado"].includes(filter) && c.risk_level !== filter) return false;
    }
    if (consultantFilter) {
      if (consultantFilter === "unassigned" && c.consultant_id) return false;
      if (consultantFilter !== "unassigned" && c.consultant_id !== consultantFilter) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const hay = `${c.protocol || ""} ${c.patient_name || ""} ${c.city || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [cases, filter, consultantFilter, search]);

  // Split consultores/admins uma única vez para o <select> — cases pesados podem re-renderizar sem tocar essa lista.
  const consultantOptions = useMemo(() => ({
    consultores: consultants.filter((c) => (c.role || "consultor") === "consultor"),
    admins: consultants.filter((c) => c.role === "admin"),
  }), [consultants]);

  // Emergenciais pinados durante Modo Plantão — memoizados para evitar recomputo em cada scroll/hover.
  const plantaoBuckets = useMemo(() => {
    if (!plantaoOn) return { emergentActive: [], rest: filtered };
    const emergentActive = filtered.filter((c) => c.risk_level === "emergencial" && c.status !== "concluido");
    const rest = filtered.filter((c) => !(c.risk_level === "emergencial" && c.status !== "concluido"));
    return { emergentActive, rest };
  }, [plantaoOn, filtered]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      {/* Iter 68.18 — Gate de aceite dos termos comerciais RAF pra consultor.
          Bloqueia todo o dashboard até o aceite formal. */}
      {currentUser && currentUser.role === "consultor" && !currentUser.terms_accepted_at && (
        <ConsultorTermsGate
          consultorName={currentUser.name}
          onAccepted={refreshUser}
        />
      )}
      {currentUser && (currentUser.role !== "consultor" || currentUser.terms_accepted_at) && (
      <>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Painel do consultor</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1 flex items-center gap-3">
            <Users className="w-8 h-8 text-slate-400" strokeWidth={1.5} /> Fila de atendimentos
          </h1>
        </div>        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/consultor/filho-presente" data-testid="fp-consultor-tab" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold shadow hover:opacity-90 transition">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20">💛</span>
            Filho Presente
          </Link>
          <a href="/consultor/central-atendimento" data-testid="central-lia-cta" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-violet-600 to-[#0B2B62] text-white text-sm font-semibold shadow hover:opacity-90 transition">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20">🤖</span>
            Central de Atendimento LIA
          </a>
          <div className="flex items-center gap-1 border border-slate-200 rounded-md p-0.5 bg-white" data-testid="days-filter">
            {[
              [null, "Tudo"],
              [7, "7d"],
              [30, "30d"],
              [90, "90d"],
            ].map(([d, l]) => (
              <button key={String(d)} data-testid={`days-${d ?? "all"}`}
                onClick={() => setDays(d)}
                className={`text-xs px-2.5 py-1.5 rounded transition-colors ${days === d ? "bg-[#0B2B62] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <button data-testid="export-csv-btn" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-sm text-slate-700 rounded-md hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        </div>
      </div>

      {/* Iter 68.17 — TOPO agora são os indicadores (Total/Ativos/Emerg/Concluídos +
          Taxa Conversão + Resposta Clínica + Casos por Risco + Substâncias +
          Casos por Dia). As ferramentas (Comissões → Profissionais → Plantões)
          ficam ABAIXO dos indicadores. */}
      {/* Iter 68.21 — Badge de capacidade (cap=5 casos ativos por consultor). */}
      {capacity && !capacity.is_admin && (
        <div className="mb-4 inline-flex items-center gap-3 p-3 rounded-lg border-2 bg-white"
             style={{
               borderColor: capacity.remaining === 0 ? "#DC2626"
                          : capacity.remaining <= 1 ? "#B45309" : "#059669",
             }}
             data-testid="consultor-capacity-badge">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
            Sua carga
          </div>
          <div className="font-display font-bold text-2xl"
               style={{
                 color: capacity.remaining === 0 ? "#DC2626"
                      : capacity.remaining <= 1 ? "#B45309" : "#059669",
               }}>
            {capacity.active}/{capacity.cap}
          </div>
          <div className="text-xs text-slate-600">
            {capacity.remaining === 0
              ? "Limite atingido — conclua 1 caso pra assumir outro."
              : `Você pode assumir mais ${capacity.remaining} caso(s)`}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Total" value={stats?.total ?? cases.length} />
        <Stat label="Ativos" value={stats?.active ?? stats_counts.active} accent="#0B2B62" />
        <Stat label="Emergenciais ativos" value={stats_counts.emerg} accent="#DC2626" />
        <Stat label="Concluídos" value={stats?.concluidos ?? stats_counts.closed} accent="#16A34A" />
      </div>

      {stats && (
        <div className="grid md:grid-cols-4 gap-3 mb-6">
          <div className="p-5 rounded-xl bg-white border border-slate-200 md:col-span-1" data-testid="metric-response">
            <div className="text-xs uppercase tracking-widest text-slate-500">Taxa de conversão</div>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-24 h-24 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[
                      { name: "Internação", value: stats.conversion?.admitted || 0, fill: "#16A34A" },
                      { name: "Não internação", value: stats.conversion?.not_admitted || 0, fill: "#EA580C" },
                    ]} dataKey="value" innerRadius={28} outerRadius={44} paddingAngle={2}>
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="font-display font-bold text-lg text-[#0B2B62]">{stats.conversion?.conversion_rate != null ? `${stats.conversion.conversion_rate}%` : "—"}</div>
                  </div>
                </div>
              </div>
              <div className="text-xs space-y-1 flex-1">
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-600" />Internação <b className="ml-auto">{stats.conversion?.admitted || 0}</b></div>
                <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-600" />Não intern. <b className="ml-auto">{stats.conversion?.not_admitted || 0}</b></div>
                <div className="pt-1 text-[10px] text-slate-400 border-t border-slate-100">Taxa entre casos concluídos</div>
              </div>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-white border border-slate-200 md:col-span-1" data-testid="metric-clinic-response">
            <div className="text-xs uppercase tracking-widest text-slate-500">Resposta clínica</div>
            <div className="mt-2 space-y-1.5 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="text-slate-500 text-xs">Envio → Leitura</span>
                <span className="font-display font-semibold text-[#0B2B62]">{stats.response?.avg_sent_to_read_min != null ? `${stats.response.avg_sent_to_read_min} min` : "—"}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-slate-500 text-xs">Leitura → Resposta</span>
                <span className="font-display font-semibold text-[#0B2B62]">{stats.response?.avg_read_to_response_min != null ? `${stats.response.avg_read_to_response_min} min` : "—"}</span>
              </div>
              <div className="flex items-baseline justify-between pt-1.5 border-t border-slate-100">
                <span className="text-slate-500 text-xs">Envio → Resposta</span>
                <span className="font-display font-bold text-[#0B2B62]">{stats.response?.avg_sent_to_response_min != null ? `${stats.response.avg_sent_to_response_min} min` : "—"}</span>
              </div>
              <div className="text-[10px] text-slate-400 pt-1">
                {stats.response?.sent || 0} envios · {stats.response?.read || 0} lidos · {stats.response?.responded || 0} respondidos
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 md:col-span-1" data-testid="chart-risk">
            <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-3"><BarChart3 className="w-3.5 h-3.5" /> Casos por risco</div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={stats.by_risk.map(r => ({ ...r, label: r.level.charAt(0).toUpperCase()+r.level.slice(1) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} width={20} />
                <Tooltip cursor={{ fill: '#F8FAFC' }} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Bar dataKey="count" radius={[6,6,0,0]}>
                  {stats.by_risk.map((entry) => {
                    const c = { emergencial: "#DC2626", alta: "#EA580C", moderada: "#EAB308", programado: "#16A34A" }[entry.level];
                    return <Cell key={entry.level} fill={c} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 md:col-span-1" data-testid="chart-substance">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Substâncias</div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={stats.by_substance} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="substance" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} width={70} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Bar dataKey="count" fill="#0B2B62" radius={[0,6,6,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 md:col-span-1" data-testid="chart-timeline">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Casos por dia</div>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={stats.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={(v)=>v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} width={20} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#0B2B62" strokeWidth={2} dot={{ r: 3, fill: '#0B2B62' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Iter 68.17 — Bloco de ferramentas do consultor DEPOIS dos indicadores.
          Ordem pedida: 1) Comissões, 2) Profissionais (Agenda), 3) Plantões
          (Minha escala + Próximos plantões + Próximos atendimentos). */}
      <section className="mb-8" data-testid="consultor-tools-block">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Minhas ferramentas</div>
        <ConsultantFinanceWidget />
        <CentralCasesWidget />
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="consultor-agenda-section">
          <ConsultorAgendaWidget />
        </div>
        <MyScheduleWidget />
        <NextPlantoesWidget />
        <NextAppointmentsWidget />
      </section>

      {streak && streak.streak_days > 0 && (
        <div className="mb-4 rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-4 flex items-center gap-4" data-testid="streak-badge">
          <div className="text-4xl">🔥</div>
          <div className="flex-1">
            <div className="text-[11px] font-bold uppercase tracking-widest text-orange-800">Sequência de atividade</div>
            <div className="font-display font-bold text-2xl text-[#0B2B62] mt-0.5">
              {streak.streak_days} {streak.streak_days === 1 ? "dia" : "dias"} seguidos
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {streak.active_days_60d} dia(s) ativo(s) nos últimos 60 · última atividade: {streak.last_active ? new Date(streak.last_active).toLocaleDateString("pt-BR") : "—"}
              {streak.onboarded && <span className="ml-2 text-green-700 font-semibold">· perfil completo ✓</span>}
            </div>
          </div>
        </div>
      )}

      {invites.length > 0 && (
        <div className="mb-4 rounded-xl border-2 border-blue-500 bg-blue-50 p-4" data-testid="invite-panel">
          <div className="text-[11px] font-bold uppercase tracking-widest text-blue-800 mb-2">
            Convite de plantão pendente
          </div>
          {invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-4 flex-wrap py-2">
              <div>
                <div className="font-display font-semibold text-[#0B2B62]">
                  {inv.invited_by_name} pediu que você cubra: {inv.reason}
                </div>
                <div className="text-xs text-slate-600">Expira: {new Date(inv.expires_at).toLocaleString("pt-BR")}</div>
              </div>
              <button
                data-testid={`accept-invite-${inv.id}`}
                onClick={() => acceptInvite(inv.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700"
              >
                Aceitar plantão
              </button>
            </div>
          ))}
        </div>
      )}

      {onboarding && !onboarding.dismissed && onboarding.done < onboarding.total && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="onboarding-checklist">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B2B62]">Bem-vindo(a) à rede RAF Direct</div>
              <div className="font-display font-bold text-lg text-[#0B2B62] mt-1">
                Complete seu perfil para começar
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {onboarding.done} de {onboarding.total} passos concluídos
              </div>
            </div>
            <button
              onClick={dismissOnboarding}
              data-testid="onboarding-dismiss"
              className="text-xs text-slate-500 hover:text-slate-800"
            >Dispensar</button>
          </div>
          <div className="w-full h-1.5 rounded-full bg-slate-100 mb-4 overflow-hidden">
            <div className="h-full bg-[#0B2B62] transition-all"
                 style={{ width: `${(onboarding.done / onboarding.total) * 100}%` }} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {onboarding.items.map((it) => (
              <Link
                key={it.key}
                to={it.action_url}
                data-testid={`onboarding-${it.key}`}
                className={`p-3 border rounded-md flex items-start gap-3 transition-colors ${it.done ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200 hover:bg-slate-100"}`}
              >
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${it.done ? "bg-green-500 text-white" : "bg-white border-2 border-slate-300"}`}>
                  {it.done ? "✓" : ""}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#0B2B62]">{it.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{it.help}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {kickHistory && kickHistory.count > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="kick-history-widget">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-amber-800">Histórico de plantão · autoavaliação</div>
              <div className="font-display font-bold text-lg text-[#0B2B62] mt-1">
                Você foi retirado do plantão {kickHistory.count} {kickHistory.count === 1 ? "vez" : "vezes"} nos últimos 30 dias
              </div>
              <div className="text-xs text-slate-600 mt-1">
                Estes eventos ficam visíveis apenas para você. Se recorrente, converse com o administrador RAF.
              </div>
            </div>
          </div>
          <div className="mt-3 space-y-1">
            {kickHistory.events.slice(0, 5).map((e, i) => (
              <div key={i} className="text-xs text-slate-700">
                <span className="font-mono text-slate-500">
                  {new Date(e.at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
                {" — por "}
                <b>{e.actor_name || "Admin RAF"}</b>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4 items-center border-b border-slate-200 pb-3">
        {[
          ["all", "Todos"], ["active", "Ativos"], ["emergencial", "🔴 Emergencial"],
          ["alta", "🟠 Alta"], ["moderada", "🟡 Moderada"], ["programado", "🟢 Programado"], ["closed", "Concluídos"],
        ].map(([k, l]) => (
          <button key={k} data-testid={`filter-${k}`} onClick={() => setFilter(k)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            data-testid="dashboard-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por protocolo, paciente ou cidade..."
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-[#0B2B62]"
          />
        </div>
        <select
          data-testid="consultant-filter"
          value={consultantFilter}
          onChange={(e) => setConsultantFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white outline-none focus:ring-2 focus:ring-[#0B2B62]"
        >
          <option value="">Todos (consultores + admin)</option>
          <option value="unassigned">Sem consultor</option>
          {(() => {
            const { consultores, admins } = consultantOptions;
            return (
              <>
                {consultores.length > 0 && (
                  <optgroup label="Consultores">
                    {consultores.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </optgroup>
                )}
                {admins.length > 0 && (
                  <optgroup label="Admins">
                    {admins.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} · admin</option>
                    ))}
                  </optgroup>
                )}
              </>
            );
          })()}
        </select>
        {(search || consultantFilter) && (
          <button
            data-testid="clear-filters"
            onClick={() => { setSearch(""); setConsultantFilter(""); }}
            className="text-xs text-slate-500 hover:text-[#0B2B62] px-2"
          >Limpar</button>
        )}
        <div className="text-xs text-slate-500 ml-auto">Mostrando {filtered.length} de {cases.length}</div>
      </div>

      {loading ? <p className="text-slate-500">Carregando…</p> : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl">
          <Activity className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-2">Sem casos neste filtro.</p>
        </div>
      ) : (() => {
        const { emergentActive, rest } = plantaoBuckets;
        return (
          <>
            {plantaoOn && emergentActive.length > 0 && (
              <div className="mb-4 rounded-xl border-2 border-red-500 bg-red-50 overflow-hidden" data-testid="plantao-emergency-panel">
                <div className="px-4 py-2 bg-red-600 text-white flex items-center gap-2 text-sm font-semibold">
                  <Siren className="w-4 h-4 animate-pulse" />
                  Modo Plantão · {emergentActive.length} caso{emergentActive.length > 1 ? "s" : ""} emergencial{emergentActive.length > 1 ? "is" : ""} ativo{emergentActive.length > 1 ? "s" : ""}
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <tbody>
                    {emergentActive.map((c) => (
                      <tr key={c.id} className="border-b border-red-100 last:border-0 hover:bg-red-100/40 transition-colors" data-testid={`plantao-row-${c.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-red-700 font-semibold whitespace-nowrap">{c.protocol}</td>
                        <td className="px-4 py-3"><div className="font-medium text-red-900 whitespace-nowrap">{c.patient_name}</div><div className="text-xs text-red-700/70 whitespace-nowrap">{c.patient_age} anos · {c.city}</div>{c.family_intent && <IntentBadge intent={c.family_intent} compact />}</td>
                        <td className="px-4 py-3 text-red-800 text-xs whitespace-nowrap">{c.substance}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Link to={`/consultor/caso/${c.id}`} className="text-red-700 font-bold hover:underline">Atender agora →</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 whitespace-nowrap">
                    <th className="px-4 py-3">Protocolo</th>
                    <th className="px-4 py-3">Paciente</th>
                    <th className="px-4 py-3">Cidade</th>
                    <th className="px-4 py-3">Substância</th>
                    <th className="px-4 py-3">Risco</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Na etapa</th>
                    <th className="px-4 py-3">Criado</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`consultant-row-${c.id}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{c.protocol}</td>
                      <td className="px-4 py-3"><div className="font-medium text-slate-800 whitespace-nowrap">{c.patient_name}</div><div className="text-xs text-slate-500 whitespace-nowrap">{c.patient_age} anos</div>{c.family_intent && <IntentBadge intent={c.family_intent} compact />}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.city}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.substance}</td>
                      <td className="px-4 py-3"><RiskBadge level={c.risk_level} size="sm" /></td>
                      <td className="px-4 py-3 text-slate-700 text-xs">{STATUS_LABEL[c.status] || c.status}</td>
                      <td className="px-4 py-3 text-xs" data-testid={`sla-${c.id}`}>{(() => {
                        const s = computeSla(c);
                        if (s.level === "none") return <span className="text-slate-400">—</span>;
                        const color = s.level === "red" ? "text-red-600" : s.level === "yellow" ? "text-amber-600" : "text-green-600";
                        const label = s.hours < 1 ? `${Math.round(s.hours * 60)}m` : `${s.hours.toFixed(1)}h`;
                        return (
                          <span className={`inline-flex items-center gap-1 font-mono ${color}`} data-sla-level={s.level}>
                            <Clock className="w-3 h-3" /> {label}
                            {s.meta && <span className="text-slate-400">/{s.meta}h</span>}
                          </span>
                        );
                      })()}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString("pt-BR")}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/consultor/caso/${c.id}`} className="text-[#0B2B62] font-semibold hover:underline">Abrir →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        );
      })()}
      </>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent = "#0B2B62" }) => (
  <div className="p-5 rounded-xl bg-white border border-slate-200">
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display font-bold text-3xl mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

// Iter 68.19 — Badge que aparece na linha do caso na fila do consultor,
// mostrando a intenção declarada pela família ao iniciar a triagem.
const INTENT_META = {
  triagem:    { label: "Emergência",       color: "#DC2626", bg: "#FEE2E2" },
  orientacao: { label: "Só orientação",    color: "#0B2B62", bg: "#EFF6FF" },
  internacao: { label: "Quer internar",    color: "#059669", bg: "#ECFDF5" },
  orcamento:  { label: "Só orçamento",     color: "#B45309", bg: "#FEF3C7" },
};
const IntentBadge = ({ intent, compact = false }) => {
  const m = INTENT_META[intent];
  if (!m) return null;
  return (
    <span data-testid={`intent-badge-${intent}`}
      className={`inline-flex items-center gap-1 rounded-full font-bold border ${compact ? "text-[9px] px-1.5 py-0.5 mt-1" : "text-xs px-2 py-1"}`}
      style={{ color: m.color, borderColor: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

/**
 * ConsultorTermsGate — Aceite formal dos termos comerciais RAF pelo consultor.
 * Iter 68.18: 4 consentimentos (comissão, exclusividade RAF, banimento por
 * atendimento externo, LGPD/segredo do paciente) + aceite geral. Diferente do
 * profissional (que atende via vídeo) e da clínica (que recebe presencialmente),
 * o consultor faz TRIAGEM e DIRECIONAMENTO — não atende paciente diretamente.
 */
const ConsultorTermsGate = ({ consultorName, onAccepted }) => {
  const [accepted, setAccepted] = useState(false);
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [c3, setC3] = useState(false);
  const [c4, setC4] = useState(false);
  const [saving, setSaving] = useState(false);
  const allChecked = accepted && c1 && c2 && c3 && c4;

  const submit = async () => {
    if (!allChecked) { toast.error("Marque todos os consentimentos antes de continuar."); return; }
    setSaving(true);
    try {
      await api.post("/consultant/me/accept-terms");
      toast.success("Termos aceitos. Bem-vindo(a) à equipe RAF Direct!");
      onAccepted && onAccepted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar aceite");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="consultor-terms-gate">
      <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1.5">
        <Scale className="w-3.5 h-3.5" /> Termos comerciais · Consultor RAF Direct
      </div>
      <h1 className="font-display font-bold text-[#0B2B62] text-3xl mt-1">
        Olá, {consultorName}. Leia e aceite antes de assumir casos.
      </h1>
      <p className="text-slate-600 mt-2 max-w-2xl leading-relaxed text-left">
        Estes termos regem sua atuação como <b>Consultor RAF Direct</b> — triagem, dossiê
        clínico e direcionamento seguro entre famílias, profissionais e unidades parceiras.
        Sem o aceite formal, seu login não libera a fila de casos.
      </p>

      <div className="mt-6 space-y-4">
        <div className="p-5 rounded-lg border-2 border-[#0B2B62] bg-slate-50">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2">
            <Users className="w-5 h-5" /> Papel do consultor (importante)
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            O consultor <b>NÃO faz atendimento clínico</b> ao paciente. Sua função é
            acolher a família na entrada, aplicar a triagem, montar o dossiê, indicar
            profissionais/unidades compatíveis e acompanhar a jornada até a internação
            (Unidade Parceira) ou consulta paga (Profissional). Todo contato com família
            acontece <b>DENTRO da plataforma</b> — chat, WhatsApp integrado, e-mail RAF.
          </p>
        </div>

        <div className="p-5 rounded-lg border-2 border-emerald-500 bg-emerald-50">
          <div className="font-display font-bold text-emerald-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5" /> Comissão do consultor
          </div>
          <p className="text-sm text-emerald-900 mt-2 leading-relaxed">
            Reconheço que recebo <b>10% do valor da matrícula</b> de cada paciente
            direcionado por mim e efetivamente internado em Unidade Parceira RAF. Nas
            consultas com profissionais credenciados, a RAF define o repasse conforme
            política vigente. Não recebo salário fixo — a remuneração é 100% por
            resultado, paga via PIX no dia 10 do mês seguinte à internação/consulta.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={c1} onChange={(e) => setC1(e.target.checked)}
                   data-testid="consultor-terms-comissao" className="mt-1" />
            <span className="text-xs text-emerald-900 font-semibold">
              Aceito o modelo 10% da matrícula (repasse mensal via PIX no dia 10).
            </span>
          </label>
        </div>

        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-700" /> Exclusividade RAF + Plantão
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            Comprometo-me a atuar <b>exclusivamente pela plataforma RAF</b> quando estiver
            em Modo Plantão (SLA de resposta 15 min). Fora do plantão sou livre pra
            trabalhar em outras redes/atividades. Reconheço que se sair do plantão sem
            avisar (kick automático), acumulo strikes que impactam meu ranking público.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={c2} onChange={(e) => setC2(e.target.checked)}
                   data-testid="consultor-terms-exclusividade" className="mt-1" />
            <span className="text-xs text-slate-800 font-semibold">
              Comprometo-me com exclusividade RAF durante o Modo Plantão e SLA de 15 min.
            </span>
          </label>
        </div>

        <div className="p-5 rounded-lg border border-red-300 bg-red-50">
          <div className="font-display font-bold text-red-800 flex items-center gap-2">
            <Ban className="w-5 h-5" /> Banimento por captura externa
          </div>
          <p className="text-sm text-red-900 mt-2 leading-relaxed">
            <b>Cláusula grave:</b> se eu direcionar família/paciente triado pela RAF para
            fora da plataforma (WhatsApp pessoal, clínica não parceira, consultório
            próprio ou de terceiros, comissão paralela), serei <b>banido em definitivo</b>
            da rede + medidas legais cabíveis: cobrança das comissões devidas, indenização
            por quebra contratual e comunicação ao conselho de classe quando aplicável.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={c3} onChange={(e) => setC3(e.target.checked)}
                   data-testid="consultor-terms-banimento" className="mt-1" />
            <span className="text-xs text-red-900 font-semibold">
              Estou ciente do banimento definitivo em caso de captura externa de casos.
            </span>
          </label>
        </div>

        <div className="p-5 rounded-lg border border-slate-200 bg-white">
          <div className="font-display font-bold text-[#0B2B62] flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-700" /> LGPD + Segredo profissional
          </div>
          <p className="text-sm text-slate-700 mt-2 leading-relaxed">
            Comprometo-me a manter <b>sigilo absoluto</b> sobre dados sensíveis das
            famílias (nome do paciente, histórico clínico, informações de tratamento),
            usá-los apenas para triagem/direcionamento dentro da plataforma e nunca
            compartilhar por canais externos. Reconheço que todos os meus acessos são
            registrados no audit log hash-chained da RAF pra fins de compliance LGPD.
          </p>
          <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={c4} onChange={(e) => setC4(e.target.checked)}
                   data-testid="consultor-terms-lgpd" className="mt-1" />
            <span className="text-xs text-slate-800 font-semibold">
              Comprometo-me com o sigilo LGPD e o uso restrito dos dados dentro da RAF.
            </span>
          </label>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-lg border border-amber-300 bg-amber-50">
        <label className="inline-flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
                 data-testid="consultor-terms-final" className="mt-1" />
          <span className="text-sm text-amber-900 font-semibold leading-relaxed">
            Li integralmente e <b>aceito</b> todos os termos acima. Estou ciente de que o
            aceite fica registrado com data/hora para fins contratuais e auditoria RAF.
          </span>
        </label>
      </div>

      <button onClick={submit} disabled={!allChecked || saving}
              data-testid="consultor-terms-accept-btn"
              className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        Aceitar termos e entrar na fila RAF
      </button>
    </div>
  );
};

export default ConsultantDashboard;
